<template>
  <div class="logistics-product-lines-editor">
    <div class="product-lines-toolbar">
      <el-select
        v-model="batchProductTypes"
        multiple
        filterable
        collapse-tags
        clearable
        size="small"
        placeholder="批量选择产品类型"
        class="batch-product-select"
      >
        <el-option
          v-for="product in productList"
          :key="product.productType"
          :label="product.productName"
          :value="product.productType"
        ></el-option>
      </el-select>
      <el-button size="small" plain @click="applyBatchProducts">批量添加</el-button>
      <el-button size="small" plain icon="el-icon-plus" @click="addLine">添加行</el-button>
    </div>
    <el-table :data="innerLines" border size="mini" class="product-lines-table">
      <el-table-column label="产品类型" min-width="150">
        <template slot-scope="scope">
          <el-select
            v-model="scope.row.productType"
            filterable
            clearable
            size="mini"
            placeholder="请选择"
            style="width: 100%"
            @change="val => onProductTypeChange(scope.$index, val)"
          >
            <el-option
              v-for="product in productList"
              :key="product.productType"
              :label="product.productName"
              :value="product.productType"
            ></el-option>
          </el-select>
        </template>
      </el-table-column>
      <el-table-column label="产品名称" min-width="140">
        <template slot-scope="scope">
          <el-input v-model="scope.row.productName" size="mini" clearable placeholder="产品名称"></el-input>
        </template>
      </el-table-column>
      <el-table-column label="单价" width="100" align="right">
        <template slot-scope="scope">
          <el-input v-model="scope.row.unitPrice" size="mini" placeholder="0.00" @input="syncLineAmount(scope.$index)"></el-input>
        </template>
      </el-table-column>
      <el-table-column label="采购数量" width="100" align="right">
        <template slot-scope="scope">
          <el-input v-model="scope.row.quantity" size="mini" placeholder="0" @input="syncLineAmount(scope.$index)"></el-input>
        </template>
      </el-table-column>
      <el-table-column label="行金额" width="90" align="right">
        <template slot-scope="scope">{{ formatAmount(scope.row.lineAmount) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="60" align="center">
        <template slot-scope="scope">
          <el-button
            type="text"
            size="mini"
            :disabled="innerLines.length <= 1"
            @click="removeLine(scope.$index)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import {
  calcLineAmount,
  createEmptyProductLine,
  normalizeProductLine,
  normalizeProductLines,
} from "../../utils/logisticsFormHelpers";

export default {
  name: "LogisticsProductLinesEditor",
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    productList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      innerLines: [],
      batchProductTypes: [],
      syncing: false,
    };
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(val) {
        if (this.syncing) return;
        const normalized = normalizeProductLines(val, this.productList);
        this.innerLines = normalized.length ? normalized : [createEmptyProductLine()];
      },
    },
    innerLines: {
      deep: true,
      handler(val) {
        this.syncing = true;
        this.$emit("input", (val || []).map(line => ({ ...line })));
        this.$nextTick(() => {
          this.syncing = false;
        });
      },
    },
  },
  methods: {
    formatAmount(value) {
      const num = parseFloat(value);
      if (Number.isNaN(num)) return "0.00";
      return num.toFixed(2);
    },
    onProductTypeChange(index, productType) {
      const product = (this.productList || []).find(item => String(item.productType) === String(productType));
      const line = this.innerLines[index];
      if (!line) return;
      line.productName = product ? product.productName || "" : "";
      this.syncLineAmount(index);
    },
    syncLineAmount(index) {
      const line = this.innerLines[index];
      if (!line) return;
      line.lineAmount = calcLineAmount(line.unitPrice, line.quantity);
    },
    addLine() {
      this.innerLines.push(createEmptyProductLine());
    },
    removeLine(index) {
      if (this.innerLines.length <= 1) return;
      this.innerLines.splice(index, 1);
    },
    applyBatchProducts() {
      const types = this.batchProductTypes || [];
      if (!types.length) {
        this.$message.warning("请先选择要批量添加的产品类型");
        return;
      }
      const existingTypes = new Set(
        this.innerLines
          .filter(line => line.productType !== "" && line.productType != null)
          .map(line => String(line.productType))
      );
      let added = 0;
      types.forEach(productType => {
        if (existingTypes.has(String(productType))) return;
        this.innerLines.push(normalizeProductLine({ productType }, this.productList));
        existingTypes.add(String(productType));
        added += 1;
      });
      if (!added) {
        this.$message.info("所选产品已在明细中");
        return;
      }
      this.batchProductTypes = [];
      this.$message.success(`已添加 ${added} 个产品`);
    },
    validate() {
      const lines = this.innerLines || [];
      if (!lines.length) return "请至少添加一个产品";
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line.productType === "" || line.productType == null) {
          return `第 ${i + 1} 行请选择产品类型`;
        }
        if (!String(line.productName || "").trim()) {
          return `第 ${i + 1} 行产品名称不能为空`;
        }
        const qty = parseInt(line.quantity, 10);
        if (!qty || qty <= 0) {
          return `第 ${i + 1} 行采购数量须大于 0`;
        }
      }
      return "";
    },
  },
};
</script>

<style lang="scss" scoped>
.logistics-product-lines-editor {
  .product-lines-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  .batch-product-select {
    flex: 1;
    min-width: 220px;
  }

  .product-lines-table {
    width: 100%;
  }
}
</style>
